import React from 'react'
import { Link } from 'react-router-dom'
import SuperUserAccountPageUI from '../../../templates/SuperUserAccountPageUI'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import CardUI from '../../../widgets/layout/CardUI'
import LRTableActionsUI from '../../../widgets/layout/LRTableActionsUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import EmptyListUI from '../../../widgets/output/EmptyListUI'
import ErrorMessageUI from '../../../widgets/form/ErrorMessageUI'
import SuccessMessageUI from '../../../widgets/form/SuccessMessageUI'
import ButtonTinyUI from '../../../widgets/action/ButtonTinyUI'
import NACHStageModalUI from './NACHStageModalUI'
import NACHSendEmailModalUI from './NACHSendEmailModalUI'
import './NACHDetailsPage.css'

export const formatDate = (dateVal) => {
  if (dateVal === null || dateVal === undefined || dateVal === '' || dateVal === '-' || dateVal === 'null' || dateVal === 'undefined') {
    return '-'
  }
  const s = String(dateVal).trim()
  if (!s || s === '-') return '-'

  // If already DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    return s
  }

  // If YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [year, month, day] = s.split('-')
    return `${day}/${month}/${year}`
  }

  // If timestamp (millis) or ISO string (e.g. 2026-10-31T00:00:00Z)
  const d = !isNaN(Number(s)) ? new Date(Number(s)) : new Date(s)
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }

  return s
}

export const formatAmount = (val) => {
  if (val === null || val === undefined || val === '' || val === '-' || val === 'NaN') {
    return '-'
  }
  let s = String(val).trim()
  if (!s || s === '-') return '-'
  if (s.startsWith('₹')) return s
  if (!s.includes(',') && !isNaN(Number(s))) {
    s = Number(s).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }
  return `₹ ${s}`
}

const formatStage = (stage) => {
  if (!stage) return 'Pending'
  const s = String(stage).trim().toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const getMissingEmailRequirements = (target) => {
  if (!target) return []
  const bank = target.bankDetails || {}
  const missing = []

  const rawAmt = String(target.amount || '').replace(/,/g, '').replace(/₹/g, '').trim()
  const numAmt = Number(rawAmt)
  if (!rawAmt || isNaN(numAmt) || numAmt <= 0) {
    missing.push('Amount')
  }

  const startDate = target.startDate ? String(target.startDate).trim() : ''
  if (!startDate || startDate === '-') {
    missing.push('Start Date')
  }

  const endDate = target.endDate ? String(target.endDate).trim() : ''
  if (!endDate || endDate === '-') {
    missing.push('End Date')
  }

  const holderName = target.holderName || bank.holderName ? String(target.holderName || bank.holderName).trim() : ''
  if (!holderName) {
    missing.push('A/c Holder Name')
  }

  const bankName = target.bankName || bank.bankName ? String(target.bankName || bank.bankName).trim() : ''
  if (!bankName) {
    missing.push('Bank Name')
  }

  const accountNumber = target.accountNumber || bank.accountNumber ? String(target.accountNumber || bank.accountNumber).trim() : ''
  if (!accountNumber) {
    missing.push('Account Number')
  }

  const ifsc = target.ifsc || bank.ifsc ? String(target.ifsc || bank.ifsc).trim() : ''
  if (!ifsc) {
    missing.push('IFSC Code')
  }

  const micr = target.micr || bank.micr ? String(target.micr || bank.micr).trim() : ''
  if (!micr) {
    missing.push('MICR Code')
  }

  const accountType = target.accountType || bank.accountType ? String(target.accountType || bank.accountType).trim() : ''
  if (!accountType || accountType === '-') {
    missing.push('Account Type')
  }

  return missing
}

export const areAllDetailsFilled = (mandate) => {
  if (!mandate || !mandate.urn) return false
  return getMissingEmailRequirements(mandate).length === 0
}

const getLoansCoveredText = (loanIds, loanIdToLrnMap) => {
  if (!loanIds || !Array.isArray(loanIds) || loanIds.length === 0) {
    return '-'
  }
  const lrns = loanIds
    .map((id) => (loanIdToLrnMap && loanIdToLrnMap[id]) || id)
    .filter(Boolean)
  return lrns.length > 0 ? lrns.join(', ') : '-'
}

export const formatAccountType = (type) => {
  if (!type) return '-'
  const t = String(type).trim().toUpperCase()
  if (t === '11' || t === 'CA' || t.includes('CURRENT')) return 'Current Account'
  if (t === '13' || t === 'CC' || t.includes('CREDIT')) return 'Cash Credit'
  return type
}

const DetailRow = ({ label, children }) => (
  <div className='g_flex_h g_flex_middle g_m_bottom_50' style={{ minHeight: '30px' }}>
    <div className='nach-detail-label'>
      {label}
    </div>
    <div style={{ flex: 1, fontSize: '13px' }}>
      {children}
    </div>
  </div>
)

const NACHDetailsPageUI = ({
  context,
  account,
  mandateList,
  errorList,
  successMsg,
  onRefreshClick,
  // Expandable cards & LRN mapping
  expandedUrns = {},
  onToggleExpand,
  loanIdToLrnMap = {},
  // Inline row editing
  editingUrn,
  editForm,
  editLoading,
  onRowEditClick,
  onCancelRowEdit,
  onEditFormChange,
  onSaveRowClick,
  // Stage dropdown & actions
  onStageSelectChange,
  emailSendingUrn,
  onSendEmailClick,
  // Send email confirmation modal
  sendEmailModalOpen,
  sendEmailModalMandate,
  onSendEmailModalCancel,
  onSendEmailModalConfirm,
  // Stage modal
  stageModalOpen,
  stageModalMandate,
  stageModalTargetStage,
  stageModalUmrn,
  stageModalDate,
  stageModalErrorList,
  stageModalBusy,
  onStageModalCancel,
  onStageModalUmrnChange,
  onStageModalDateChange,
  onStageModalSubmit,
  // Downloads
  onDownloadMandateClick,
  onDownloadExcelClick
}) => (
  <SuperUserAccountPageUI
    context={context}
    className='pg_su_acc'
    backLink='/su/accounts'
    headerText={account ? account.accountName : ''}
    account={account}
    page='nach'
    dimension='w1600'
    centered={!account || !mandateList}
  >
    {(!account || !mandateList) && <SpinnerComponentUI />}

    {account && mandateList && (
      <div style={{ width: '100%' }}>
        <LRTableActionsUI
          location='top'
          left={
            <div className='g_flex_h g_flex_middle'>
              <IconButtonUI icon='icon-refresh' onClick={onRefreshClick} />
              <span className='g_m_left_100 g_large'>
                <b>NACH Mandates</b>
              </span>
            </div>
          }
          right={
            <div className='g_flex_h g_flex_right g_flex_middle'>
              <span className='g_muted g_small'>
                {mandateList.length} {mandateList.length === 1 ? 'Mandate' : 'Mandates'} Total
              </span>
            </div>
          }
        />

        {/* Workflow guidance note */}
        <div className='nach-guide-note'>
          <div className='nach-guide-note-title'>NACH Mandate Workflow Guide</div>
          <div className='nach-guide-note-body'>
            • <b>Pending:</b> Fill all Bank details &amp; enter the Start Date. Once all fields are completed, click <b>Send Email</b> to submit the registration package to Worldline.<br />
            • <b>Submitted:</b> Mandate documents have been emailed to Worldline for processing with NPCI.<br />
            • <b>Registered:</b> When registration succeeds, update stage to <b>Registered</b> and input the 16–20 character UMRN received from NPCI.<br />
            • <b>Cancelled:</b> If a mandate is revoked or replaced, update stage to <b>Cancelled</b> with the cancellation date.
          </div>
        </div>

        {errorList && errorList.length > 0 && (
          <div className='g_m_bottom_100'>
            <ErrorMessageUI errorList={errorList} />
          </div>
        )}

        {successMsg && (
          <div className='g_m_bottom_100'>
            <SuccessMessageUI successMsg={successMsg} />
          </div>
        )}

        {mandateList.length > 0 ? (
          <div>
            {mandateList.map((mandate, idx) => {
              const isExpanded = Boolean(expandedUrns[mandate.urn])
              const isEditing = editingUrn === mandate.urn
              const bank = mandate.bankDetails || {}
              const currentStage = (mandate.stage || 'pending').toLowerCase()
              const allFilled = areAllDetailsFilled(mandate)

              if (!isExpanded) {
                return (
                  <CardUI
                    className='scard nach-mandate-card'
                    style={{ width: '100%', padding: '12px 16px' }}
                    key={mandate.urn || idx}
                  >
                    <div
                      className='g_flex_h g_flex_middle g_flex_space_between g_cursor_pointer'
                      onClick={() => onToggleExpand(mandate.urn)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') onToggleExpand(mandate.urn) }}
                    >
                      <div className='g_flex_h g_flex_middle' style={{ gap: '12px' }}>
                        <span className='icon icon-angle-right' style={{ fontSize: '15px' }}></span>
                        <span style={{ fontSize: '14px' }}>
                          <b>URN:</b> {mandate.urn}
                        </span>
                        {(mandate.backfilled === true || mandate.backfilled === 'true') && (
                          <span className='nach-bf-badge'>
                            Backfilled
                          </span>
                        )}
                        {(mandate.active === true || mandate.active === 'true') && (
                          <span className='g_muted g_small'>(Active)</span>
                        )}
                      </div>

                      <div className='g_flex_h g_flex_middle' style={{ gap: '24px', fontSize: '13px' }}>
                        <div>
                          <span className='g_muted'>Amount: </span>
                          <b>{formatAmount(mandate.amount)}</b>
                        </div>
                        <div>
                          <span className='g_muted'>Start Date: </span>
                          <span>{formatDate(mandate.startDate)}</span>
                        </div>
                        <div>
                          <span className='g_muted'>End Date: </span>
                          <span>{formatDate(mandate.endDate)}</span>
                        </div>
                        <div>
                          <span className='nach-stage-capsule'>
                            {formatStage(currentStage)}
                          </span>
                        </div>
                        <span className='icon icon-angle-down' style={{ fontSize: '15px' }}></span>
                      </div>
                    </div>
                  </CardUI>
                )
              }

              return (
                <CardUI
                  className='scard nach-mandate-card'
                  style={{ width: '100%', padding: '16px 20px' }}
                  key={mandate.urn || idx}
                >
                  {/* TOP BAR: URN on left, Action buttons on right */}
                  <div
                    className='g_flex_h g_flex_space_between g_flex_middle g_m_bottom_100 nach-card-header-bar'
                  >
                    <div
                      className='g_flex_h g_flex_middle g_cursor_pointer'
                      onClick={() => onToggleExpand(mandate.urn)}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter') onToggleExpand(mandate.urn) }}
                      style={{ gap: '10px' }}
                    >
                      <span className='icon icon-angle-down' style={{ fontSize: '15px' }}></span>
                      <span style={{ fontSize: '15px' }}>
                        <b>URN:</b> <b>{mandate.urn}</b>
                      </span>
                      {(mandate.backfilled === true || mandate.backfilled === 'true') && (
                        <span className='nach-bf-badge'>
                          Backfilled
                        </span>
                      )}
                      {(mandate.active === true || mandate.active === 'true') && (
                        <span className='g_muted g_small'>(Active)</span>
                      )}
                    </div>

                    <div className='g_flex_h g_flex_middle' style={{ gap: '10px' }}>
                      {/* Stage Dropdown (styled as a capsule) */}
                      <select
                        className='nach-stage-select'
                        value={currentStage}
                        onChange={(e) => onStageSelectChange(mandate, e.target.value)}
                        disabled={isEditing}
                      >
                        <option value='pending'>Pending</option>
                        {currentStage === 'submitted' && <option value='submitted'>Submitted</option>}
                        <option value='registered'>Registered</option>
                        <option value='cancelled'>Cancelled</option>
                      </select>

                      {/* Edit button (pending stage only, when not editing) */}
                      {!isEditing && currentStage === 'pending' && (
                        <ButtonTinyUI
                          label='Edit'
                          onClick={() => onRowEditClick(mandate)}
                          className='nach-action-btn'
                        />
                      )}

                      {/* Send Email button (pending stage only, when not editing) */}
                      {!isEditing && currentStage === 'pending' && (
                        allFilled ? (
                          <ButtonTinyUI
                            label={emailSendingUrn === mandate.urn ? 'Sending...' : 'Send Email'}
                            onClick={() => onSendEmailClick(mandate)}
                            disabled={emailSendingUrn === mandate.urn}
                            className='nach-action-btn'
                          />
                        ) : (
                          <ButtonTinyUI
                            label='Send Email'
                            disabled={true}
                            onClick={() => {}}
                            title={`To enable Send Email, click Edit and set: ${getMissingEmailRequirements(mandate).join(', ')}`}
                            className='nach-action-btn nach-action-btn-disabled'
                          />
                        )
                      )}

                      {/* Collapse arrow icon button */}
                      <span
                        role='button'
                        tabIndex={0}
                        className='g_cursor_pointer g_m_left_50'
                        onClick={() => onToggleExpand(mandate.urn)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onToggleExpand(mandate.urn) }}
                        title='Collapse'
                        style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 8px' }}
                      >
                        <span className='icon icon-angle-up' style={{ fontSize: '18px' }}></span>
                      </span>
                    </div>
                  </div>

                  {/* Prompt banner when in pending stage and some email requirements are missing */}
                  {!isEditing && currentStage === 'pending' && !allFilled && (
                    <div className='nach-pending-email-banner'>
                      <span className='icon icon-info-sign' style={{ marginRight: '8px', fontSize: '14px' }}></span>
                      <span>
                        To send the registration email to Worldline, click <b>Edit</b> and provide: <b>{getMissingEmailRequirements(mandate).join(', ')}</b>.
                      </span>
                    </div>
                  )}

                  {/* TWO COLUMN CARDS: Left (Mandate details) & Right (Bank details) */}
                  <div className='g_m_top_100' style={{ display: 'flex', flexDirection: 'row', width: '100%', gap: '16px' }}>
                    {/* LEFT COLUMN CARD */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CardUI className='scard' title='Mandate Details'>
                        <DetailRow label='URN & Downloads'>
                          <div>
                            <b>{mandate.urn}</b>
                          </div>
                          <div className='g_m_top_25' style={{ fontSize: '12px' }}>
                            <span
                              role='button'
                              tabIndex={0}
                              className='g_cursor_pointer'
                              onClick={() => onDownloadMandateClick(mandate)}
                              onKeyDown={(e) => { if (e.key === 'Enter') onDownloadMandateClick(mandate) }}
                              style={{ color: '#007bff', textDecoration: 'underline' }}
                            >
                              Mandate Form
                            </span>
                            <span className='g_m_left_50 g_m_right_50' style={{ color: '#aaa' }}>|</span>
                            <span
                              role='button'
                              tabIndex={0}
                              className='g_cursor_pointer'
                              onClick={() => onDownloadExcelClick(mandate)}
                              onKeyDown={(e) => { if (e.key === 'Enter') onDownloadExcelClick(mandate) }}
                              style={{ color: '#007bff', textDecoration: 'underline' }}
                            >
                              Generated Excel
                            </span>
                          </div>
                        </DetailRow>

                        <DetailRow label='Amount'>
                          {isEditing ? (
                            <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                              <span className='g_m_right_25'>₹</span>
                              <input
                                type='number'
                                step='any'
                                className='form-control input-sm'
                                style={{ width: '140px' }}
                                value={editForm.amount}
                                onChange={(e) => onEditFormChange('amount', e.target.value)}
                                placeholder='Rupees'
                              />
                            </div>
                          ) : (
                            <b>{formatAmount(mandate.amount)}</b>
                          )}
                        </DetailRow>

                        <DetailRow label='Loans Covered'>
                          {getLoansCoveredText(mandate.loanIds, loanIdToLrnMap)}
                        </DetailRow>

                        <DetailRow label='CP Compliance'>
                          {mandate.loanId ? (
                            <Link
                              to={`/su/loans/${mandate.loanId}/compliance/cp#cp-row-16`}
                              style={{ color: '#007bff', textDecoration: 'underline' }}
                              title='View CP Documents (16: Scanned form of NACH & 17:  Registration of NACH)'
                            >
                              {'View CP Documents (16: Scanned form of NACH & 17:  Registration of NACH)'}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailRow>

                        <DetailRow label='Start Date'>
                          {isEditing ? (
                            <input
                              type='date'
                              className='form-control input-sm'
                              style={{ width: '160px' }}
                              value={editForm.startDate}
                              onChange={(e) => onEditFormChange('startDate', e.target.value)}
                            />
                          ) : (
                            formatDate(mandate.startDate)
                          )}
                        </DetailRow>

                        <DetailRow label='End Date'>
                          {isEditing ? (
                            <input
                              type='date'
                              className='form-control input-sm'
                              style={{ width: '160px' }}
                              value={editForm.endDate}
                              onChange={(e) => onEditFormChange('endDate', e.target.value)}
                            />
                          ) : (
                            formatDate(mandate.endDate)
                          )}
                        </DetailRow>

                        <DetailRow label='UMRN'>
                          {mandate.umrn || '-'}
                        </DetailRow>

                        <DetailRow label='Previous URN'>
                          {mandate.previousUrn || '-'}
                        </DetailRow>

                        <DetailRow label='Submitted Date'>
                          {formatDate(mandate.submittedDate)}
                        </DetailRow>

                        <DetailRow label='Registered Date'>
                          {formatDate(mandate.registeredDate)}
                        </DetailRow>

                        <DetailRow label='Cancellation Date'>
                          {formatDate(mandate.cancelledDate)}
                        </DetailRow>

                        <DetailRow label='Stage'>
                          <span className='nach-stage-capsule'>
                            {formatStage(currentStage)}
                          </span>
                        </DetailRow>
                      </CardUI>
                    </div>

                    {/* RIGHT COLUMN CARD */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <CardUI className='scard' title='Bank Details'>
                        <DetailRow label='A/c Holder Name'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '220px' }}
                              value={editForm.holderName}
                              onChange={(e) => onEditFormChange('holderName', e.target.value)}
                              placeholder='A/c Holder Name'
                            />
                          ) : (
                            bank.holderName || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='Bank Name'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '220px' }}
                              value={editForm.bankName}
                              onChange={(e) => onEditFormChange('bankName', e.target.value)}
                              placeholder='Bank Name'
                            />
                          ) : (
                            bank.bankName || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='Branch Name'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '220px' }}
                              value={editForm.branchName}
                              onChange={(e) => onEditFormChange('branchName', e.target.value)}
                              placeholder='Branch Name'
                            />
                          ) : (
                            bank.branchName || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='Account Number'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '220px' }}
                              value={editForm.accountNumber}
                              onChange={(e) => onEditFormChange('accountNumber', e.target.value)}
                              placeholder='Account Number'
                            />
                          ) : (
                            bank.accountNumber || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='IFSC Code'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '160px', textTransform: 'uppercase' }}
                              value={editForm.ifsc}
                              onChange={(e) => onEditFormChange('ifsc', e.target.value.toUpperCase())}
                              placeholder='IFSC Code'
                            />
                          ) : (
                            bank.ifsc || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='MICR Code'>
                          {isEditing ? (
                            <input
                              type='text'
                              className='form-control input-sm'
                              style={{ width: '160px' }}
                              value={editForm.micr}
                              onChange={(e) => onEditFormChange('micr', e.target.value)}
                              placeholder='MICR Code'
                            />
                          ) : (
                            bank.micr || '-'
                          )}
                        </DetailRow>

                        <DetailRow label='Account Type'>
                          {isEditing ? (
                            <select
                              className='form-control input-sm'
                              style={{ width: '160px' }}
                              value={editForm.accountType}
                              onChange={(e) => onEditFormChange('accountType', e.target.value)}
                            >
                              <option value='Current Account'>Current Account</option>
                              <option value='Cash Credit'>Cash Credit</option>
                            </select>
                          ) : (
                            formatAccountType(bank.accountType)
                          )}
                        </DetailRow>
                      </CardUI>
                    </div>
                  </div>

                  {/* BOTTOM BAR: Save & Cancel Buttons (Only in Edit mode) */}
                  {isEditing && (
                    <div
                      className='g_flex_h g_flex_space_between g_flex_middle g_m_top_100 nach-card-bottom-bar'
                    >
                      <div style={{ flex: 1, paddingRight: '16px' }}>
                        {(() => {
                          if (currentStage !== 'pending') return null
                          const missingInForm = getMissingEmailRequirements(editForm)
                          if (missingInForm.length === 0) {
                            return (
                              <div className='nach-email-ready-hint'>
                                <span className='icon icon-check' style={{ marginRight: '6px' }}></span>
                                <span>All details required to Send Email are filled.</span>
                              </div>
                            )
                          }
                          return (
                            <div className='nach-email-missing-hint'>
                              <span className='icon icon-info-sign' style={{ marginRight: '6px' }}></span>
                              <span>
                                Required to enable Send Email: <b>{missingInForm.join(', ')}</b>
                              </span>
                            </div>
                          )
                        })()}
                      </div>

                      <div className='g_flex_h g_flex_right g_flex_middle' style={{ gap: '10px' }}>
                        <ButtonTinyUI
                          label='Cancel'
                          onClick={onCancelRowEdit}
                          disabled={editLoading}
                          className='nach-action-btn'
                        />
                        <ButtonTinyUI
                          label={editLoading ? 'Saving...' : 'Save'}
                          onClick={() => onSaveRowClick(mandate)}
                          disabled={editLoading}
                          className='nach-action-btn'
                        />
                      </div>
                    </div>
                  )}
                </CardUI>
              )
            })}
          </div>
        ) : (
          <EmptyListUI context={context} createDisabledMessageLabel='No NACH Mandates Found' />
        )}

        {/* Modal for asking dates and UMRN on Stage Change (Registered / Cancelled) */}
        <NACHStageModalUI
          context={context}
          isOpen={stageModalOpen}
          mandate={stageModalMandate}
          targetStage={stageModalTargetStage}
          umrn={stageModalUmrn}
          onUmrnChange={onStageModalUmrnChange}
          date={stageModalDate}
          onDateChange={onStageModalDateChange}
          onSubmit={onStageModalSubmit}
          onCancel={onStageModalCancel}
          errorList={stageModalErrorList}
          busy={stageModalBusy}
        />

        {/* Modal for confirming NACH registration email sending */}
        <NACHSendEmailModalUI
          context={context}
          isOpen={sendEmailModalOpen}
          mandate={sendEmailModalMandate}
          account={account}
          onSubmit={onSendEmailModalConfirm}
          onCancel={onSendEmailModalCancel}
          busy={Boolean(emailSendingUrn)}
        />
      </div>
    )}
  </SuperUserAccountPageUI>
)

export default NACHDetailsPageUI
